import { DEFAULT_ROW_HEIGHT } from './theme';

/**
 * Default header height in pixels.
 */
const DEFAULT_HEADER_HEIGHT = 49;

const EMPTY_STATE_HEIGHT = 300;

const BOTTOM_SCROLLBAR_HEIGHT = 16;

/**
 * AG Grid renders a 1px header element even when the header is hidden
 * (`headerHeight: 0`). Reserve it so the body fits its rows exactly and
 * doesn't overflow into a scrollbar.
 */
const RESIDUAL_HEADER_HEIGHT = 1;

const getEstimatedTableHeight = (rowCount: number): number =>
  DEFAULT_HEADER_HEIGHT + rowCount * DEFAULT_ROW_HEIGHT;

type GetDynamicTableHeightOptions = {
  emptyStateHeight?: number;
  includeBottomScrollbarHeight?: boolean;
  /**
   * When the table's header is hidden (e.g. all column titles are empty and
   * the consumer suppresses `.ag-header`), set this to `true` to omit the
   * header's height from the returned value. Otherwise the computed height
   * leaves an empty band below the last row.
   */
  excludeHeaderHeight?: boolean;
};

export const getDynamicTableHeight = (
  rowCount: number,
  maxRows: number,
  {
    emptyStateHeight = EMPTY_STATE_HEIGHT,
    includeBottomScrollbarHeight = false,
    excludeHeaderHeight = false,
  }: GetDynamicTableHeightOptions = {}
): number => {
  const headerHeight = excludeHeaderHeight
    ? RESIDUAL_HEADER_HEIGHT
    : DEFAULT_HEADER_HEIGHT;
  if (rowCount === 0) {
    return headerHeight + emptyStateHeight;
  }
  return (
    Math.min(rowCount, maxRows) * DEFAULT_ROW_HEIGHT +
    headerHeight +
    (includeBottomScrollbarHeight ? BOTTOM_SCROLLBAR_HEIGHT : 0)
  );
};

const reorderRows = <T>(
  rows: T[],
  sourceIndex: number,
  targetIndex: number
): T[] => {
  const nextRows = [...rows];
  const [source] = nextRows.splice(sourceIndex, 1);
  nextRows.splice(targetIndex, 0, source);

  return nextRows;
};

export const tableOperations = {
  reorderRows,
  getEstimatedTableHeight,
  getDynamicTableHeight,
};
